import React, { useState } from 'react';
import { Auth } from 'aws-amplify';

interface VerificationFormProps {
  email: string;
  onVerificationSuccess: () => void;
  onBackToSignup: () => void;
}

export default function VerificationForm({ email, onVerificationSuccess, onBackToSignup }: VerificationFormProps) {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await Auth.confirmSignUp(email, verificationCode);
      onVerificationSuccess();
    } catch (err: any) {
      setError(err.message || 'An error occurred during verification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError('');

    try {
      await Auth.resendSignUp(email);
      setError(''); // Clear any previous errors
      // You could show a success message here
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification code');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-base-content mb-2">
          Verify Your Email
        </h2>
        <p className="text-base-content/70">
          We've sent a verification code to <strong>{email}</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="alert alert-error">
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Verification Code</span>
          </label>
          <input
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            className="input input-bordered w-full text-center text-lg tracking-widest"
            placeholder="Enter 6-digit code"
            maxLength={6}
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-control">
          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={isLoading || verificationCode.length !== 6}
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Verifying...
              </>
            ) : (
              'Verify Email'
            )}
          </button>
        </div>
      </form>

      <div className="mt-6 text-center space-y-4">
        <p className="text-base-content/70 text-sm">
          Didn't receive the code?
        </p>
        <button
          onClick={handleResendCode}
          className="link link-primary text-sm"
          disabled={isLoading}
        >
          Resend verification code
        </button>
        
        <div className="pt-4 border-t border-base-content/20">
          <button
            onClick={onBackToSignup}
            className="link link-primary text-sm"
            disabled={isLoading}
          >
            Back to sign up
          </button>
        </div>
      </div>
    </div>
  );
}
